import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { exportListToExcel } from 'assets/utils/file/Excel';
import { HttpStatus, defaultLimit, defaultOffset } from 'config/Api.Config';
import ContractRefundPaymentEmailTemplate from 'models/core/contractRefundPaymentEmailTemplate/Model.ContractRefundPaymentEmailTemplate';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import ContractRefundPaymentStatusTriggers from 'models/enums/ContractRefundPaymentStatusTriggers';
import contractRefundPaymentExportApi from 'models/finance/contractRefundPayment/export/Api.ContractRefundPayment.Export';
import contractRefundPaymentGenerateRemittanceApi from 'models/finance/contractRefundPayment/generateRemittance/Api.ContractRefundPayment.GenerateRemittance';
import ContractRefundPayment from 'models/finance/contractRefundPayment/Model.ContractRefundPayment';
import contractRefundPaymentEmailSendEmailApi from 'models/finance/contractRefundPaymentEmail/sendEmail/Api.ContractRefundPaymentEmail.SendEmail';
import Distributor from 'models/sales/distributor/Model.Distributor';
import { useState } from 'react';
import { financeContractRefundPaymentIndexRoute } from './Finance.ContractRefundPayment.Index';

export enum ContractRefundPaymentAction {
  INFO = 'info',
  CANCEL = 'cancel',
  SEND_EMAIL = 'sendEmail',
}
export type VerifyContractRefundPaymentAction = Utils.VerifyExtends<
  Module.Finance.ContractRefundPayment.Actions,
  Utils.ValueOf<typeof ContractRefundPaymentAction>
>;
export function useContractRefundPaymentRouter() {
  return usePageRouter<Module.Finance.ContractRefundPayment.Params, Module.Finance.ContractRefundPayment.Query>({
    route: financeContractRefundPaymentIndexRoute,
  });
}
export default function useContractRefundPayment() {
  const { lang } = getLocales();
  const pageRouter = useContractRefundPaymentRouter();
  const { permissions, user } = useAuthStore();

  const [isExporting, setIsExporting] = useState<boolean>(false);

  const canView = permissions.CREATE_CONTRACTREFUNDPAYMENT || permissions.CANCEL_CONTRACTREFUNDPAYMENT;

  //#region Api calls
  const listApi = useApi(
    {
      action: ContractRefundPayment.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !canView,
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const findApi = useApi(
    {
      action: ContractRefundPayment.find,
      wait: !canView || !pageRouter.params.id,
      body: {
        id: Number(pageRouter.params.id),
      },
    },
    [pageRouter.params.id]
  );

  const cancelApi = useApi({
    action: ContractRefundPayment.cancel,
    wait: true,
    callback: () => {
      pageRouter.updateParams({});
      listApi.execute((b) => b);
    },
  });
  //#endregion

  const reload = () => {
    if (pageRouter.params.id) findApi.execute((b) => b);
    listApi.execute((b) => b);
    pageRouter.updateParams({});
  };

  const userApi = useApi({ action: User.list, body: { isDisabled: false }, wait: true });
  const dealersApi = useApi({
    action: Dealer.list,
    default: {
      id: user?.dealers?.map((d) => d.id) ?? [],
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
    wait: true,
  });

  const distributorsApi = useApi({ action: Distributor.list, wait: true });

  const emailTemplatesApi = useApi({
    action: ContractRefundPaymentEmailTemplate.list,
    body: {
      contractRefundPaymentStatusTrigger: [ContractRefundPaymentStatusTriggers.CONTRACT_REFUNDS_REMITTANCE_ADVICE],
    },
  });
  const emailTemplates = emailTemplatesApi.payload?.data;

  async function sendEmail(emailTemplate: Model.IContractRefundPaymentEmailTemplate) {
    const res = await contractRefundPaymentEmailSendEmailApi({
      params: { contractRefundPaymentId: parseInt(pageRouter.params.id) },
      body: emailTemplate,
    });

    if (res.status === HttpStatus.ok) {
      addLog({ success: lang.emailSuccessfullySent });
      reload();
    } else {
      addLog({ error: res.message });
    }
  }

  // tracked per payment so several remittances can be regenerated at the same time
  const [generatingRemittanceIds, setGeneratingRemittanceIds] = useState<number[]>([]);
  const isGeneratingRemittance = (contractRefundPaymentId: number) =>
    generatingRemittanceIds.includes(contractRefundPaymentId);

  async function generateRemittance(contractRefundPaymentId: number) {
    setGeneratingRemittanceIds((prev) => [...prev, contractRefundPaymentId]);

    const res = await contractRefundPaymentGenerateRemittanceApi({ contractRefundPaymentId });

    if (res.status === HttpStatus.ok) {
      addLog({ success: lang.remittanceSuccessfullyGenerated });
      setGeneratingRemittanceIds((prev) => prev.filter((id) => id !== contractRefundPaymentId));
      reload();
    } else {
      addLog({ error: res.message || lang.error });
      setGeneratingRemittanceIds((prev) => prev.filter((id) => id !== contractRefundPaymentId));
    }
  }

  async function exportToExcel() {
    setIsExporting(true);
    try {
      await exportListToExcel(contractRefundPaymentExportApi, pageRouter.query.list, lang.contractsPaymentHistory);
    } finally {
      setIsExporting(false);
    }
  }

  return {
    listApi,
    userApi,
    dealersApi,
    distributorsApi,
    pageRouter,
    permissions,
    selected: pageRouter.params.id ? findApi?.payload : null,
    canView,
    reload,
    cancelApi,
    emailTemplates,
    emailTemplatesApi,
    sendEmail,
    generateRemittance,
    isGeneratingRemittance,
    exportToExcel,
    isExporting,
  };
}

export function useContractRefundPaymentProvider() {
  return useDataProvider<ReturnType<typeof useContractRefundPayment>>();
}
